import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { APIURL, TZ } from '../../../Global';
import AddItemBase from '../AddLabTest/AddGroup/AddItemBase';
import { useAlert } from 'react-alert';
import { FaCheck, FaTrash } from 'react-icons/fa';

const AddPolicy = ({ group, handleSuccess, onClose }) => {
    const alert = useAlert();
    const [selectedItems, setSelectedItems] = useState([]);
    const [options, setOptions] = useState([]);
    const [initialPolicies, setInitialPolicies] = useState([]);

    useEffect(() => {
        if (group?.sharable_policy) {
            const initial = group.sharable_policy.map(item => 
                typeof item === 'object' ? item.policy : item
            );
            setInitialPolicies(initial);
            setSelectedItems(
                initial.map(policy => ({
                    policy,
                    added: true
                }))
            );
        }
    }, [group]);

    useEffect(() => {
        const fetchData = async () => {
            const tokenString = sessionStorage.getItem('usertoken');
            const str = tokenString?.replace(/["]+/g, '');

            try {
                const response = await axios.get(`${APIURL}/api/v1/staff/hospital-group/?group_policy=group_policy`, {
                    headers: { Authorization: 'Token ' + str },
                });

                if (response.data.status === 'success') {
                    setOptions((response.data.policy || []).map(item => ({
                        policy: item,
                    })));
                }
            } catch (error) {
                console.error('Error fetching policies:', error);
                setOptions([]);
                alert.error('Error fetching policies.');
            }
        };

        fetchData();
    }, [alert]);

    const formatPolicy = (policy) => {
        return policy.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const handleSelectItem = (item) => {
        setSelectedItems(prev => {
            const itemValue = item.policy;
            const exists = prev.some(i => i.policy === itemValue);
            const isInitial = initialPolicies.includes(itemValue);

            if (exists) {
                // Don't allow deselecting initially added policies
                if (isInitial) return prev;
                return prev.filter(i => i.policy !== itemValue);
            } else {
                return [...prev, { ...item, added: false }];
            }
        });
    };

    const handleRemoveItem = (item) => {
        // Don't allow removing initially added policies
        if (item.added) return;
        setSelectedItems(prev => prev.filter(i => i.policy !== item.policy));
    };

    const handleSubmit = async () => {
        if (!group?.id) {
            alert.error('Group is mandatory');
            return;
        }

        const payload = {
            policy_method: 'add',
            group_id: group.id,
            sharable_policy: selectedItems.map(item => item.policy)
        };

        try {
            const tokenString = sessionStorage.getItem('usertoken');
            const str = tokenString?.replace(/["]+/g, '');

            const res = await axios.put(
                `${APIURL}/api/v1/staff/hospital-group/?group_id=${group.id}&${TZ}`,
                payload,
                { headers: { Authorization: 'Token ' + str } }
            );

            if (res.status === 200 && res.data.status === 'success') {
                alert.success('Policies updated successfully!');
                handleSuccess();
                onClose();
            } else {
                alert.error(res.data.message || 'Failed to update policies.');
            }
        } catch (err) {
            alert.error('Failed to update policies. Please try again.');
            console.error('API Error:', err);
        }
    };

    const filteredOptions = (searchTerm) => {
        return options.filter(item =>
            !searchTerm ||
            formatPolicy(item.policy).toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const renderItemsList = (searchTerm) => {
        const items = filteredOptions(searchTerm);
        return items.length > 0 ? (
            items.map((item) => {
                const isSelected = selectedItems.some(selected => selected.policy === item.policy);
                const isInitial = initialPolicies.includes(item.policy);
                
                return (
                    <div
                        key={item.policy}
                        className={`item-card ${isSelected ? 'selected' : ''} ${isInitial ? 'initial-item' : ''}`}
                        onClick={() => !isInitial && handleSelectItem(item)}
                        title={isInitial ? "This policy was previously added and cannot be removed" : ""}
                    >
                        <div className="item-content">
                            {formatPolicy(item.policy)}
                            {isInitial && <span className="initial-badge">(Already added)</span>}
                        </div>
                        {isSelected && <FaCheck className="selected-icon" />}
                    </div>
                );
            })
        ) : (
            <div className="empty-message">
                {searchTerm
                    ? 'No policies match your search'
                    : 'No policies available'}
            </div>
        );
    };

    const renderSelectedList = () => {
        return selectedItems.length > 0 ? (
            selectedItems.map((item) => (
                <div key={item.policy} className={`selected-item ${item.added ? 'initial-item' : ''}`}>
                    <span>
                        {formatPolicy(item.policy)}
                        {item.added && <span className="added-badge">(Already added)</span>}
                    </span>
                    {!item.added && (
                        <FaTrash
                            className="remove-icon"
                            onClick={() => handleRemoveItem(item)}
                        />
                    )}
                </div>
            ))
        ) : (
            <div className="empty-message">
                No policies selected
            </div>
        );
    };

    return (
        <AddItemBase
            type="feature"
            group={group}
            onClose={onClose}
            handleSuccess={handleSuccess}
            renderItemsList={renderItemsList}
            renderSelectedList={renderSelectedList}
            handleSubmit={handleSubmit}
            selectedItems={selectedItems}
        />
    );
};

export default AddPolicy;